import type { FeatureCollection, Geometry } from 'geojson'
import type { GeoJsonProperties } from 'geojson'
import type { FilterSpecification, GeoJSONSource, Map as MapLibreMap } from 'maplibre-gl'

import type { Layer, RefreshOutcome } from '../layer'
import { labelFont } from './slots'
import { emptyCollection } from './spc'

/**
 * The fourth `Layer` kind: polygons we fetch, style from their own attributes
 * and draw ourselves.
 *
 * Every Phase 2 overlay is a raster somebody upstream rendered for us. These
 * are not — SPC publishes attributes, so the layer can honour the product's
 * own colors, print its own short code inside the area, and answer a tap out
 * of the browser's memory with no network at all.
 *
 * The colors come from the data via `['get', 'fill']` rather than from a table
 * here, which is the same principle that stops this app re-rendering
 * nowCOAST's imagery: a feature that arrives without a color does not get one
 * invented, it gets dropped by the parser before it reaches this file.
 *
 * Timer discipline is `WmsStaticLayer`'s and for the same reason: no `TIME` in
 * the request means the content goes stale in place.
 */

export interface GeoJsonOptions<P extends GeoJsonProperties> {
  id: string
  /**
   * One query, or several fetched together. With an array `parse` is handed
   * the payloads in the same order, and a failure of any one of them is a
   * failure of the pass: NHC's watch/warning lines are judged against the
   * cones fetched beside them, and a stale line drawn because the cone fetch
   * failed is exactly the product this app keeps refusing to draw.
   */
  url: string | readonly string[]
  /**
   * Upstream JSON to a collection whose properties this file can paint. `now`
   * is passed rather than read inside, so the validity rule is testable
   * without mocking the clock — and so it re-runs on every refresh and every
   * re-mount rather than being decided once at boot.
   */
  parse(json: unknown, now: number): FeatureCollection<Geometry, P>
  attribution: string
  opacity: number
  /** 0 disables the refresh timer. */
  refreshSeconds?: number
  /**
   * False for outline-only products. The fill layer is still mounted, at
   * opacity 0: it is what a tap inside the outline hits. An MD is a 2 px
   * stroke around a state-sized polygon, and requiring the finger to land on
   * the stroke itself would make it unopenable on a phone — while a visible
   * fill would be this app asserting a magnitude the MD service does not
   * publish. `queryRenderedFeatures` includes a layer at opacity 0 and
   * excludes one at `visibility: none`, which is exactly the behaviour wanted.
   */
  filled?: boolean
  /** Draw each feature's `short` inside it, where the basemap has glyphs. */
  labelled?: boolean
  /** The default; a feature's own `width` property overrides it. */
  lineWidth?: number
  /**
   * The fill drawn at this fraction of the layer's opacity. A forecast cone
   * is a wash that the track, the points and the coastal warnings sit on, and
   * one opacity for all of them either buries the cone's contents or makes
   * the lines too faint to read.
   */
  fillFactor?: number
  /**
   * The collection carries lines and points as well as polygons. Adds a
   * circle layer for the points — `fill` inside, `stroke` around, `short`
   * drawn on top and never dropped for a collision — and pins each style
   * layer to the geometry it is for. The ordinary label layer then draws a
   * point's `name`, beside it, instead of an area's `short`. Off for the SPC
   * rows, whose style layers are unchanged by this option existing.
   */
  points?: boolean
}

/** MapLibre's tiled features report Polygon for a MultiPolygon too; both are
 *  listed so the filter does not depend on which. */
const POLYGONS: FilterSpecification = [
  'in',
  ['geometry-type'],
  ['literal', ['Polygon', 'MultiPolygon']],
]
const POINTS: FilterSpecification = ['in', ['geometry-type'], ['literal', ['Point', 'MultiPoint']]]
const NOT_POINTS: FilterSpecification = ['!', POINTS]
const NAMED_POINTS: FilterSpecification = ['all', POINTS, ['has', 'name']]

/**
 * How long to wait before retrying when the first fetch failed.
 *
 * Shorter than any product's own cadence on purpose. A layer the user has just
 * switched on that missed its one fetch draws nothing, and "nothing" is
 * exactly what a quiet day looks like — so the failure is invisible and would
 * otherwise last the whole refresh interval, five minutes for an outlook.
 */
const RETRY_SECONDS = 20

export class GeoJsonLayer<P extends GeoJsonProperties> implements Layer {
  readonly id: string
  readonly times = null
  private map: MapLibreMap | null = null
  private opacity: number
  private visible = true
  private timer: ReturnType<typeof setInterval> | null = null
  /** The cadence `timer` is running at, so a retry can be replaced by the
   *  product's own once data has landed. */
  private timerSeconds = 0
  /** The upstream payload, kept raw so the validity rule can re-run on it. */
  private raw: unknown = null
  private loaded = false
  /** When `raw` last landed; 0 before the first success. */
  private fetchedAt = 0
  private labelId: string | null = null
  private pointLabelId: string | null = null

  constructor(private readonly opts: GeoJsonOptions<P>) {
    this.id = opts.id
    this.opacity = opts.opacity
  }

  private get key(): string {
    return `wx:${this.id}`
  }

  private get fillId(): string {
    return `${this.key}:fill`
  }

  private get lineId(): string {
    return `${this.key}:line`
  }

  private get circleId(): string {
    return `${this.key}:circle`
  }

  private get points(): boolean {
    return this.opts.points === true
  }

  private get filled(): boolean {
    return this.opts.filled !== false
  }

  /** 0 for an outline-only layer: the fill is a hit target, not a paint. */
  private fillOpacity(): number {
    return this.filled ? this.opacity * (this.opts.fillFactor ?? 1) : 0
  }

  /**
   * Fetch once. Memoized, so a basemap switch — which destroys every
   * style-owned source — re-mounts from what is already in hand and makes no
   * request.
   *
   * `async` with a `try` around the whole body, deliberately: `tests/setup.ts`
   * installs a `fetch` that throws *synchronously*, so a `.catch()` chained
   * onto the call would never run and the error would escape. A layer that
   * cannot load draws an empty collection, exactly as `Layer` asks.
   */
  async load(): Promise<void> {
    if (this.loaded) return
    await this.fetchOnce()
    this.loaded = true
  }

  /**
   * One fetch, never throwing. A failure is announced: an empty overlay is
   * indistinguishable from a product with no area today, so going quiet here
   * leaves nobody a thread to pull — the same reasoning as the `/api/place`
   * warning in `MapView`.
   */
  private async fetchOnce(): Promise<boolean> {
    const { url } = this.opts
    try {
      const raw = typeof url === 'string' ? await fetchJson(url) : await Promise.all(url.map(fetchJson))
      this.raw = raw
      this.fetchedAt = Date.now()
      return true
    } catch (e) {
      console.warn(`overlay ${this.id}: ${String(url)} failed; drawing nothing`, e)
      return false
    }
  }

  /** The features that may draw *now*. Re-derived rather than cached: a layer
   *  left open across an expiry empties itself instead of showing a product
   *  that has lapsed. */
  private collection(): FeatureCollection<Geometry, P> {
    return this.raw === null ? emptyCollection<P>() : this.opts.parse(this.raw, Date.now())
  }

  add(map: MapLibreMap, beforeId?: string): void {
    this.map = map
    if (map.getSource(this.key)) return
    map.addSource(this.key, {
      type: 'geojson',
      data: this.collection(),
      attribution: this.opts.attribution,
    })

    const visibility = this.visible ? 'visible' : 'none'
    map.addLayer(
      {
        id: this.fillId,
        type: 'fill',
        source: this.key,
        layout: {
          visibility,
          // Not decoration, and a layout property rather than a paint one.
          // Without it MapLibre draws in source order, which today happens to
          // be ascending severity and therefore correct — a coincidence of how
          // SPC writes the file, and one that would paint TSTM over ENH the
          // day it stopped holding.
          'fill-sort-key': ['get', 'rank'],
        },
        ...(this.points ? { filter: POLYGONS } : {}),
        paint: {
          // `stroke` for the unfilled case: the layer is invisible, but
          // MapLibre validates the expression either way and every feature
          // carries a stroke.
          'fill-color': ['get', this.filled ? 'fill' : 'stroke'],
          'fill-opacity': this.fillOpacity(),
        },
      },
      beforeId,
    )

    map.addLayer(
      {
        id: this.lineId,
        type: 'line',
        source: this.key,
        layout: { visibility },
        ...(this.points ? { filter: NOT_POINTS } : {}),
        paint: {
          'line-color': ['get', 'stroke'],
          // A width is not a colour: a feature with none takes the layer's,
          // which is ours to choose either way.
          'line-width': ['coalesce', ['get', 'width'], this.opts.lineWidth ?? 1.5],
          'line-opacity': this.opacity,
        },
      },
      beforeId,
    )

    if (this.points) {
      map.addLayer(
        {
          id: this.circleId,
          type: 'circle',
          source: this.key,
          filter: POINTS,
          layout: { visibility },
          paint: {
            'circle-color': ['get', 'fill'],
            'circle-radius': 7,
            'circle-stroke-color': ['get', 'stroke'],
            'circle-stroke-width': 1.5,
            'circle-opacity': this.opacity,
            'circle-stroke-opacity': this.opacity,
          },
        },
        beforeId,
      )
    }

    // Conditional: the CARTO Voyager fallback is one raster layer declaring no
    // `glyphs`, where a symbol layer draws nothing and logs an error.
    const font = this.opts.labelled ? labelFont(map) : null
    if (font) {
      this.labelId = `${this.key}:label`
      // Note the missing `beforeId`. The fill and the line go *under* the
      // basemap's labels, because a wash of colour over place names is
      // unreadable — but MRGL is itself a label, and when two symbols collide
      // MapLibre keeps the one in the layer drawn last. Placed under the
      // basemap this loses to every city name it touches, which in practice
      // meant most areas drew no code at all. Labels belong with labels.
      map.addLayer({
        id: this.labelId,
        type: 'symbol',
        source: this.key,
        ...(this.points
          ? {
              filter: NAMED_POINTS,
              layout: {
                visibility,
                'text-field': ['get', 'name'],
                'text-font': font,
                'text-size': 12,
                'text-allow-overlap': false,
                // Clear of the 7 px circle, on its right.
                'text-anchor': 'left',
                'text-offset': [0.9, 0],
              },
            }
          : {
              layout: {
                visibility,
                'text-field': ['get', 'short'],
                'text-font': font,
                'text-size': 12,
                'text-allow-overlap': false,
              },
            }),
        paint: {
          // SPC's own colour for the category, as everywhere else here. The
          // halo is ours, and is white because that is what these products are
          // drawn on: a light basemap under a translucent fill.
          'text-color': ['get', 'stroke'],
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.5,
          'text-opacity': this.opacity,
        },
      })
    } else {
      this.labelId = null
    }

    // A point's letter is the point's meaning — H inside a circle is a
    // hurricane — so it is drawn on its circle whatever it collides with,
    // where an area's code may yield to a city name.
    if (font && this.points) {
      this.pointLabelId = `${this.key}:pointlabel`
      map.addLayer({
        id: this.pointLabelId,
        type: 'symbol',
        source: this.key,
        filter: POINTS,
        layout: {
          visibility,
          'text-field': ['get', 'short'],
          'text-font': font,
          'text-size': 10,
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': ['get', 'stroke'],
          'text-opacity': this.opacity,
        },
      })
    } else {
      this.pointLabelId = null
    }

    this.schedule()
  }

  /** Every style layer this instance owns, top-down, for removal. */
  private styleLayers(): string[] {
    const ids = [this.lineId, this.fillId]
    if (this.points) ids.unshift(this.circleId)
    if (this.labelId) ids.unshift(this.labelId)
    if (this.pointLabelId) ids.unshift(this.pointLabelId)
    return ids
  }

  remove(map: MapLibreMap): void {
    this.stop()
    for (const id of this.styleLayers()) {
      if (map.getLayer(id)) map.removeLayer(id)
    }
    if (map.getSource(this.key)) map.removeSource(this.key)
    this.labelId = null
    this.pointLabelId = null
    if (this.map === map) this.map = null
  }

  setOpacity(opacity: number): void {
    this.opacity = opacity
    const map = this.map
    if (!map) return
    if (map.getLayer(this.fillId)) {
      map.setPaintProperty(this.fillId, 'fill-opacity', this.fillOpacity())
    }
    if (map.getLayer(this.lineId)) map.setPaintProperty(this.lineId, 'line-opacity', opacity)
    if (this.labelId && map.getLayer(this.labelId)) {
      map.setPaintProperty(this.labelId, 'text-opacity', opacity)
    }
    if (this.pointLabelId && map.getLayer(this.pointLabelId)) {
      map.setPaintProperty(this.pointLabelId, 'text-opacity', opacity)
    }
    if (this.points && map.getLayer(this.circleId)) {
      map.setPaintProperty(this.circleId, 'circle-opacity', opacity)
      map.setPaintProperty(this.circleId, 'circle-stroke-opacity', opacity)
    }
  }

  setTime(): void {
    // An outlook is a discrete product with a validity window, not a frame on
    // the shared axis. The map menu prints the window instead.
  }

  currentTime(): Date | null {
    return null
  }

  setVisible(visible: boolean): void {
    if (this.visible === visible) return
    this.visible = visible
    const map = this.map
    if (map) {
      for (const id of this.styleLayers()) {
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none')
      }
    }
    // A layer nobody can see should not be polling NOAA.
    if (visible) this.schedule()
    else this.stop()
  }

  ready(): Promise<void> {
    const map = this.map
    if (!map) return Promise.resolve()
    return new Promise((resolve) => map.once('idle', () => resolve()))
  }

  private schedule(): void {
    const seconds = this.opts.refreshSeconds ?? 0
    if (!seconds || this.timer || !this.visible) return
    // Until the first payload lands, poll faster than the product's cadence.
    this.timerSeconds = this.raw === null ? Math.min(seconds, RETRY_SECONDS) : seconds
    this.timer = setInterval(() => void this.reload(), this.timerSeconds * 1000)
  }

  private stop(): void {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
    this.timerSeconds = 0
  }

  /**
   * Catch up after the page was hidden, when the payload is past the
   * product's own cadence. A sleeping phone freezes the interval, so without
   * this an outlook or a discussion could be hours old when the app comes
   * back — still drawn, because `collection()` only drops what has *expired*,
   * and a product that has been replaced has not necessarily expired.
   */
  async refresh(canPatch: () => boolean): Promise<RefreshOutcome> {
    const seconds = this.opts.refreshSeconds ?? 0
    if (!seconds || !this.visible || !this.loaded) return 'unchanged'
    if (Date.now() - this.fetchedAt < seconds * 1000) return 'unchanged'
    this.stop()
    await this.reload(canPatch)
    this.schedule()
    return 'unchanged'
  }

  /**
   * Re-fetch in place. `setData`, not remove-and-re-add, or every polygon
   * flashes on each tick. A failed refresh keeps the previous collection —
   * but only until its own features expire, because `collection()` re-runs the
   * validity rule against the current clock.
   */
  private async reload(canPatch: () => boolean = () => true): Promise<void> {
    // A failure keeps whatever is on the map — but only until those features
    // expire, because `collection()` re-runs the validity rule against the
    // current clock on every tick.
    const got = await this.fetchOnce()
    // Mid basemap switch the source is gone anyway, and `style.load` re-adds
    // it from the payload that has just landed.
    const source = canPatch()
      ? (this.map?.getSource(this.key) as GeoJSONSource | undefined)
      : undefined
    source?.setData?.(this.collection())
    // Drop back to the product's own cadence once there is something to show.
    if (got && this.timerSeconds !== (this.opts.refreshSeconds ?? 0)) {
      this.stop()
      this.schedule()
    }
  }
}

async function fetchJson(url: string): Promise<unknown> {
  const resp = await fetch(url)
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
  return resp.json()
}
